import { useMemo, useState } from 'react'
import type { VisitedSource } from '../types'
import './SourcesList.css'

interface SourcesListProps {
  sources: VisitedSource[]
}

function normaliseHostname(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '')
  } catch {
    return url
  }
}

function getUrlPath(url: string): string {
  try {
    const u = new URL(url)
    const path = u.pathname && u.pathname !== '/' ? u.pathname : ''
    const search = u.search || ''
    const fullPath = path + search
    // Return path beyond domain, truncated if too long
    return fullPath.length > 60 ? `${fullPath.slice(0, 60)}…` : fullPath
  } catch {
    return ''
  }
}

export default function SourcesList({ sources }: SourcesListProps) {
  const [query, setQuery] = useState('')

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (sources.length === 0) return sources
    if (!q) return sources
    return sources.filter((s) => {
      const domain = normaliseHostname(s.url)
      const path = getUrlPath(s.url)
      const hay = `${domain} ${path} ${s.url}`.toLowerCase()
      return hay.includes(q)
    })
  }, [query, sources])

  // Important: only return after hooks to keep hook order stable across renders.
  if (sources.length === 0) {
    return null
  }

  return (
    <div className="sources-panel">
      <div className="sources-header">
        <h3>
          Sources ({filtered.length}
          {filtered.length !== sources.length ? ` of ${sources.length}` : ''})
        </h3>
        <div className="sources-hint">Scrollable list of unique URLs visited during research.</div>
      </div>

      <div className="sources-controls">
        <input
          className="sources-search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search sources…"
          aria-label="Search sources"
        />
      </div>

      <div className="sources-scroll">
        <div className="sources-grid">
          {filtered.map((source, idx) => {
            const domain = normaliseHostname(source.url)
            const urlPath = getUrlPath(source.url)
            return (
              <div key={idx} className="source-card">
                <div className="source-header">
                  <div className="source-domain-primary">{domain}</div>
                  {source.agent_count !== undefined && (
                    <span className="source-count">
                      {source.agent_count} agent{source.agent_count !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>
                {urlPath && (
                  <a
                    href={source.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="source-path"
                    title={source.url}
                  >
                    {urlPath}
                  </a>
                )}
                {source.relevance && <div className="source-relevance">{source.relevance}</div>}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

